import React, { FC, useState, useContext } from "react";
import { useNavigate } from "react-router";

import { EditDrillSessionValidation } from "./EditDrillSessionValidation";
import { SATOSContentRender } from "@/app/application/SATOSContentRender";
import { useTime } from "@/app/application/TimeContext";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import {
	fromDateTimeLocalValue,
	toDateTimeLocalValue,
} from "@/features/training/lib/trainingDateUtils";
import {
	SessionKpi,
	TrainingDrillSessionStatus,
} from "@/features/training/model/Training/TrainingDrillSession";
import { CustomDeleteButton } from "@/features/training/ui";
import { KpiInputsDataGrid } from "@/features/training/pages/drill-tab/panel-right/drill-session/KpiInputsDataGrid";
import SATOSEditorField from "@/shared/forms/SATOSEditorField";
import { AlertRequest } from "@/shared/notifications/AlertRequest";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CloseIcon from "@mui/icons-material/Close";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { CancelActionButton, SaveActionButton } from "@/shared/ui";
import {
	sharedTaskDialogActionsSx,
	sharedTaskDialogContentSx,
	sharedTaskDialogHeaderSx,
	sharedTaskDialogPanelSx,
	sharedTaskDialogPaperSx,
} from "@/styles/sharedTaskDialogStyles";
import {
	Alert,
	Box,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormHelperText,
	IconButton,
	InputLabel,
	LinearProgress,
	MenuItem,
	Paper,
	Select,
	Stack,
	Switch,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { Field, type FieldProps, Form, Formik } from "formik";

import { UserSummary } from "@/model/User/UserSummary";

import {
	useMutateDeleteTrainingDrillSession,
	useMutateUpdateTrainingDrillSession,
} from "@/service/TrainingMutations";
import {
	useFetchDrill,
	useFetchDrillSession,
	useFetchUserSummariesByCurrentCompany,
} from "@/service/TrainingQueries";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


/* ──────────────────────────────────────────────────────────────────────────── */
/* Types                                                                       */
/* ──────────────────────────────────────────────────────────────────────────── */

interface EditDrillSessionProps {
	id: number;
	open: boolean;
	handleClose: () => void;
}

type FormValues = {
	Id: number;
	Date: string;
	ResponsibleId: number | "";
	Information: string;
	Comment: string;
	KpiParameters: SessionKpi[];
	Standin: boolean;
};

/* ──────────────────────────────────────────────────────────────────────────── */
/* Section header                                                              */
/* ──────────────────────────────────────────────────────────────────────────── */

const SectionHeader: FC<{
	title: React.ReactNode;
	subtitle?: React.ReactNode;
	icon?: React.ReactNode;
}> = ({ title, subtitle, icon }) => (
	<Box sx={{ mb: 1.5 }}>
		<Stack direction="row" alignItems="center" gap={1}>
			{icon}
			<Typography variant="subtitle1" fontWeight={700}>
				{title}
			</Typography>
		</Stack>
		{subtitle && (
			<Typography
				variant="caption"
				color="text.secondary"
				sx={{ display: "block", mt: 0.25 }}
			>
				{subtitle}
			</Typography>
		)}
	</Box>
);

/* ──────────────────────────────────────────────────────────────────────────── */
/* Dialog shell                                                                */
/* ──────────────────────────────────────────────────────────────────────────── */

export const EditDrillSession: FC<EditDrillSessionProps> = (props) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const closeButtonSx = {
		color: theme.palette.text.secondary,
		"&:hover": {
			color: theme.palette.text.primary,
			backgroundColor: theme.palette.action.hover,
		},
	};

	return (
		<Dialog
			onClose={props.handleClose}
			open={props.open}
			fullWidth
			maxWidth="md"
			fullScreen={isMobile}
			aria-labelledby="edit-drill-session-title"
			PaperProps={{
				sx: sharedTaskDialogPaperSx(theme, isMobile),
			}}
		>
			<DialogTitle
				id="edit-drill-session-title"
				component="div"
				sx={{
					...sharedTaskDialogHeaderSx(theme),
					position: "relative",
					px: { xs: 2, sm: 2.5 },
					pt: 2,
					pb: 1.5,
				}}
			>
				<Typography
					variant="caption"
					sx={{
						display: "block",
						fontWeight: 800,
						letterSpacing: "0.08em",
						color: "primary.main",
						mb: 0.4,
					}}
				>
					<Translate String="Auto.Features.Training.Pages.DrillTab.PanelRight.DrillSession.EditDrillSession.L163C6.TRAINING">TRAINING</Translate>
				</Typography>
				<Typography
					variant="h6"
					component="h2"
					sx={{ fontWeight: 700, color: "text.primary", pr: 5, mb: 0.75 }}
				>
					<Translate String="Training.DrillSession.Edit.Title">
						Edit Session
					</Translate>
				</Typography>
				<Typography
					variant="body2"
					color="text.secondary"
					sx={{ mt: 0.5, display: "block" }}
				>
					<Translate String="Training.DrillSession.Edit.Subtitle">
						Update session details, responsible person, and KPI parameters.
					</Translate>
				</Typography>
				<IconButton
					aria-label={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.DrillTab.PanelRight.DrillSession.EditDrillSession.L185C17.Close", "Close")}
					onClick={props.handleClose}
					size="small"
					sx={{ position: "absolute", right: 8, top: 8, ...closeButtonSx }}
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>
			<DialogContent
				sx={{
					...sharedTaskDialogContentSx(theme),
					px: { xs: 2, sm: 2.5 },
					pt: 2,
					pb: 0,
					flex: 1,
					overflow: "auto",
				}}
			>
				<EditDrillSessionForm {...props} />
			</DialogContent>
		</Dialog>
	);
};

/* ──────────────────────────────────────────────────────────────────────────── */
/* Form                                                                        */
/* ──────────────────────────────────────────────────────────────────────────── */

export const EditDrillSessionForm: FC<EditDrillSessionProps> = (props) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const navigate = useNavigate();
	const { timezone } = useTime();

	const { data: session, isLoading: isLoadingSession } = useFetchDrillSession(
		props.id,
	);
	const drillId = session?.DrillId ?? 0;
	const { data: drill, isLoading: isLoadingDrill } = useFetchDrill(drillId);
	const { data: trainerRaw, isLoading: isLoadingTrainerOptions } =
		useFetchUserSummariesByCurrentCompany();

	const trainers: UserSummary[] = Array.isArray(trainerRaw) ? trainerRaw : [];
	const { updateTrainingDrillSession } = useMutateUpdateTrainingDrillSession();
	const deleteTrainingDrillSession = useMutateDeleteTrainingDrillSession();
	const [isDeleting, setIsDeleting] = useState(false);

	if (isLoadingSession || isLoadingDrill || isLoadingTrainerOptions) {
		return (
			<Box
				sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
			>
				<LinearProgress />
			</Box>
		);
	}
	if (!session || !drill) {
		return (
			<Box
				display="flex"
				alignItems="center"
				justifyContent="center"
				minHeight={200}
			>
				<Typography variant="h6" color="text.secondary">
					<Translate String="Auto.Features.Training.Pages.DrillTab.PanelRight.DrillSession.EditDrillSession.L249C53.SessionNotFound">Session not found</Translate>
				</Typography>
			</Box>
		);
	}

	const isDisabled = session.Status === TrainingDrillSessionStatus.Archived;
	const sectionCardSx = {
		...sharedTaskDialogPanelSx(theme),
		p: { xs: 2, md: 2.5 },
	};

	const initialValues: FormValues = {
		Id: session.Id,
		Date: toDateTimeLocalValue(session.Date, timezone),
		ResponsibleId: session.Responsible?.Id ?? "",
		Information: session.Information,
		Comment: session.Comment ?? "",
		KpiParameters: session.KpiParameters,
		Standin: session.Standin ?? false,
	};

	const handleSave = async (formValues: FormValues) => {
		try {
			await updateTrainingDrillSession({
				Id: formValues.Id,
				Date: fromDateTimeLocalValue(formValues.Date, timezone).toISOString(),
				ResponsibleId: Number(formValues.ResponsibleId),
				Information: formValues.Information,
				Comment: formValues.Comment,
				KpiParameters: formValues.KpiParameters.map((k) => ({
					Id: k.Id,
					Value: k.Value,
				})),
				Standin: formValues.Standin,
			});
			props.handleClose();
			AlertRequest.success();
		} catch (error) {
			console.error(error);
			AlertRequest.failed();
		}
	};

	const handleDelete = async () => {
		if (isDeleting) {
			return;
		}

		try {
			setIsDeleting(true);
			props.handleClose();
			const confirmation = await AlertRequest.delete();
			if (!confirmation.isConfirmed) return;
			await deleteTrainingDrillSession({
				sessionId: props.id,
				drillId: drill.Id,
			});
			AlertRequest.deleteConfirmed("this training session");
			navigate(`/training/drills/${drill.Id}`);
		} catch (error) {
			console.error(error);
			void AlertRequest.failed();
		} finally {
			setIsDeleting(false);
		}
	};

	return (
		<Formik<FormValues>
			initialValues={initialValues}
			validationSchema={() => EditDrillSessionValidation(trainers)}
			onSubmit={handleSave}
			enableReinitialize
		>
			{({ values, errors, setFieldValue, isSubmitting }) => (
				<Form>
					{isDisabled && (
						<Alert
							severity="info"
							variant="outlined"
							sx={{ mb: 2, borderRadius: 2 }}
						>
							<Translate String="Training.DrillSession.Edit.ArchivedNotice">
								This session is archived. Fields are read-only.
							</Translate>
						</Alert>
					)}

					<Stack spacing={3}>
						{/* 1. Details */}
						<Paper variant="outlined" sx={sectionCardSx}>
							<SectionHeader
								title={
									<Translate String="Training.DrillSession.Edit.Section.Details">
										Details
									</Translate>
								}
								subtitle={
									<Translate String="Training.DrillSession.Edit.Section.Details.Desc">
										Schedule, responsible person and notes.
									</Translate>
								}
							/>
							<Stack spacing={2}>
								<Field name="Date">
									{({ field, meta }: FieldProps<string, FormValues>) => (
										<TextField
											{...field}
											label={
												<Translate String="Training.DrillSession.Edit.Date">
													Date
												</Translate>
											}
											type="datetime-local"
											fullWidth
											size="small"
											disabled={isDisabled}
											error={meta.touched && Boolean(meta.error)}
											helperText={meta.touched && meta.error}
											InputLabelProps={{ shrink: true }}
											sx={{
												"& .MuiOutlinedInput-root": {
													borderRadius: 2,
													bgcolor: "background.paper",
												},
											}}
										/>
									)}
								</Field>
								<Field name="ResponsibleId">
									{({
										field,
										meta,
										form,
									}: FieldProps<number | "", FormValues>) => (
										<FormControl
											fullWidth
											size="small"
											disabled={isDisabled}
											error={meta.touched && Boolean(meta.error)}
											sx={{
												"& .MuiOutlinedInput-root": {
													borderRadius: 2,
													bgcolor: "background.paper",
												},
											}}
										>
											<InputLabel>
												<Translate String="Training.DrillSession.Edit.Responsible">
													Responsible
												</Translate>
											</InputLabel>
											<Select
												{...field}
												label={
													<Translate String="Training.DrillSession.Edit.Responsible">
														Responsible
													</Translate>
												}
												value={field.value ?? ""}
												onChange={(e) => {
													void form.setFieldValue(
														"ResponsibleId",
														e.target.value === "" ? "" : Number(e.target.value),
													);
												}}
												displayEmpty
											>
												<MenuItem value="">
													<em>
														<Translate String="Training.DrillSession.Edit.SelectResponsible">
															Select responsible
														</Translate>
													</em>
												</MenuItem>
												{trainers.map((x) => (
													<MenuItem key={x.Id} value={x.Id}>
														{x.Name}
													</MenuItem>
												))}
											</Select>
											{meta.touched && meta.error && (
												<FormHelperText>{meta.error}</FormHelperText>
											)}
										</FormControl>
									)}
								</Field>
								{isDisabled ? (
									<>
										<SectionHeader
											title={
												<Translate String="Training.DrillSession.Edit.Information">
													Information
												</Translate>
											}
										/>
										<SATOSContentRender content={session.Information} />
									</>
								) : (
									<SATOSEditorField
										id="information"
										name="Information"
										label="Information"
										disabled={isDisabled}
										style={{ minWidth: "100%" }}
										imageUploadDisabled={true}
									/>
								)}
								<FormControlLabel
									labelPlacement="start"
									sx={{
										mt: 0.5,
										mr: 0,
										gap: 1,
										alignItems: "center",
										flexWrap: "nowrap",
										"& .MuiFormControlLabel-label": {
											marginRight: 0,
										},
									}}
									control={
										<Switch
											checked={values.Standin}
											onChange={(e) => {
												void setFieldValue("Standin", e.target.checked);
											}}
											name="Standin"
											color="primary"
											disabled={isDisabled}
										/>
									}
									label={
										<Translate String="Training.DrillSession.Edit.Standin">
											Stand in
										</Translate>
									}
								/>
							</Stack>
						</Paper>

						{/* 2. KPI Parameters */}
						<Paper variant="outlined" sx={sectionCardSx}>
							<SectionHeader
								title={
									<Translate String="Training.DrillSession.Edit.KpiParameters">
										KPI Parameters
									</Translate>
								}
								subtitle={
									<Translate String="Training.DrillSession.Edit.KpiParameters.Desc">
										Enter values for this session
									</Translate>
								}
							/>
							<KpiInputsDataGrid
								kpiInputs={values.KpiParameters}
								isDisabled={isDisabled}
							/>
							{typeof errors.KpiParameters === "string" &&
								errors.KpiParameters !== "updating" && (
									<Typography
										variant="body2"
										color="error.main"
										sx={{ mt: 1, textAlign: "center" }}
									>
										{errors.KpiParameters}
									</Typography>
								)}
							{values.Comment && (
								<Box
									sx={{
										mt: 2,
										pt: 2,
										borderTop: 1,
										borderColor: "divider",
									}}
								>
									<Typography
										variant="caption"
										color="text.secondary"
										fontWeight={600}
										sx={{ display: "block", mb: 0.5 }}
									>
										<Translate String="Training.DrillSession.Edit.Comment">
											Comment
										</Translate>
									</Typography>
									<Typography variant="body2">{values.Comment}</Typography>
								</Box>
							)}
						</Paper>
					</Stack>

					{/* Footer */}
					<DialogActions
						sx={{
							...sharedTaskDialogActionsSx(theme, isMobile),
							px: 0,
							pt: 2,
							mt: 3,
							flexWrap: "wrap",
							justifyContent: "space-between",
							gap: 1.5,
							display: "flex",
						}}
					>
						<CustomDeleteButton
							handleDelete={handleDelete}
							loading={isDeleting}
							disabled={isDisabled || isSubmitting || isDeleting}
							textOverride={
								<Translate String="Training.Common.Button.Delete">
									Delete
								</Translate>
							}
						/>
						<Stack direction="row" spacing={1.5}>
							<CancelActionButton
								onClick={props.handleClose}
								disabled={isSubmitting || isDeleting}
							>
								<Translate String="Procedure.Button.Cancel">Cancel</Translate>
							</CancelActionButton>
							<SaveActionButton
								type="submit"
								loading={isSubmitting}
								loadingText={
									<Translate String="LabDaily.Saving">Saving...</Translate>
								}
								disabled={
									isDisabled ||
									isSubmitting ||
									isDeleting ||
									errors.KpiParameters === "updating"
								}
							>
								<Translate String="Training.EditCourse.SaveChanges">
									Save changes
								</Translate>
							</SaveActionButton>
						</Stack>
					</DialogActions>
				</Form>
			)}
		</Formik>
	);
};
